import {
    CylinderGeometry,
    DoubleSide,
    Geometry,
    Matrix4,
    Mesh,
    MeshBasicMaterial,
    RingGeometry,
    SphereGeometry,
    Vector3,
} from 'three';
import { rotationMatrixBetweenVectors } from './matrix3';

export default class Halo {
    center: Vector3;
    normal: Vector3;
    halo_material: MeshBasicMaterial;
    halo_ring: Mesh | null;
    halo_axis: Mesh | null;
    halo_axis_geometry: Geometry | null;
    halo_ring_geometry: Geometry | null;

    constructor() {
        this.center = new Vector3();
        this.normal = new Vector3();
        this.halo_material = new MeshBasicMaterial({ color: 0xffbb00 });
        this.halo_material.side = DoubleSide;
        this.halo_material.transparent = true;
        this.halo_material.opacity = 0.6;
        this.halo_material.depthTest = false;
        this.halo_material.depthWrite = false;
        this.halo_ring = null;
        this.halo_axis = null;
        this.halo_axis_geometry = null;
        this.halo_ring_geometry = null;
    }

    show(center: Vector3, normal: Vector3) {
        // Free current geometry
        if (this.halo_axis_geometry) {
            this.halo_axis_geometry.dispose();
            this.halo_axis_geometry = null;
        }
        if (this.halo_ring_geometry) {
            this.halo_ring_geometry.dispose();
            this.halo_ring_geometry = null;
        }

        this.center.copy(center);
        this.normal.copy(normal);

        this.halo_axis_geometry = new CylinderGeometry(viewSize/500, viewSize/500, viewSize / 500.00, 32, 1, false);
        this.halo_ring_geometry = new RingGeometry(viewSize/50, viewSize / 40.0, 16, 32, 0, Math.PI * 2);

        this.halo_axis_geometry.rotateX(Math.PI/2.0);
        const nv = new Vector3(0, 0, 1);
        const R = rotationMatrixBetweenVectors(nv, this.normal);
        // Rotate geometries.
        const m4 = new Matrix4();
        m4.set(
            R.elements[0]!,
            R.elements[3]!,
            R.elements[6]!,
            0,
            R.elements[1]!,
            R.elements[4]!,
            R.elements[7]!,
            0,
            R.elements[2]!,
            R.elements[5]!,
            R.elements[8]!,
            0,
            0,
            0,
            0,
            1
        );
        this.halo_ring_geometry.applyMatrix4(m4);
        this.halo_ring_geometry.translate(this.center.x, this.center.y, this.center.z);
        this.halo_axis_geometry.applyMatrix4(m4);
        this.halo_axis_geometry.translate(this.center.x, this.center.y, this.center.z);

        if (!this.halo_ring) {
            this.halo_ring = new Mesh(this.halo_ring_geometry, this.halo_material);
            this.halo_ring.name = 'halo_ring';
            uid_halo_raynode.add(this.halo_ring);
        } else {
            this.halo_ring.geometry = this.halo_ring_geometry;
        }

        if (!this.halo_axis) {
            this.halo_axis = new Mesh(this.halo_axis_geometry, this.halo_material);
            this.halo_axis.name = 'halo_axis';
            uid_halo_raynode.add(this.halo_axis);
        } else {
            this.halo_axis.geometry = this.halo_axis_geometry;
        }

        scene.remove(uid_halo_raynode);
        scene.add(uid_halo_raynode);
    }

    hide() {
        scene.remove(uid_halo_raynode);
    }

    remove() {
        if (this.halo_axis) {
            uid_halo_raynode.remove(this.halo_axis);
            if (!Array.isArray(this.halo_axis.material))
                this.halo_axis.material.dispose();
            this.halo_axis.geometry.dispose();
        }
        if (this.halo_ring) {
            uid_halo_raynode.remove(this.halo_ring);
            if (!Array.isArray(this.halo_ring.material))
                this.halo_ring.material.dispose();
            this.halo_ring.geometry.dispose();
        }
        scene.remove(uid_halo_raynode);
    }

    extend_axis(positive_direction: boolean) {
        if (this.halo_axis) {
            uid_halo_raynode.remove(this.halo_axis);
            if (!(Array.isArray(this.halo_axis.material)))
                this.halo_axis.material.dispose();
            this.halo_axis.geometry.dispose();
        }
        this.halo_axis_geometry = new CylinderGeometry(viewSize/500, viewSize/500, viewSize / 15.00, 32, 1, false);
        this.halo_axis_geometry.rotateX(Math.PI/2.0);
        const nv = new Vector3(0, 0, 1);
        const R = rotationMatrixBetweenVectors(nv, this.normal);
        // Rotate geometries.
        const m4 = new Matrix4();
        m4.set(
            R.elements[0]!,
            R.elements[3]!,
            R.elements[6]!,
            0,
            R.elements[1]!,
            R.elements[4]!,
            R.elements[7]!,
            0,
            R.elements[2]!,
            R.elements[5]!,
            R.elements[8]!,
            0,
            0,
            0,
            0,
            1
        );
        this.halo_axis_geometry.applyMatrix4(m4);
        const offset = positive_direction
            ? viewSize / 30.00
            : (-1) * viewSize / 30.00;

        this.halo_axis_geometry.translate(this.center.x + this.normal.x * offset, this.center.y  + this.normal.y * offset, this.center.z + this.normal.z * offset);
        if (positive_direction)
            this.halo_material.color.setRGB(0.2, .9, 0.2);
        else
            this.halo_material.color.setRGB(0.9, 0.2, 0.2);

        this.halo_axis = new Mesh(this.halo_axis_geometry, this.halo_material);
        this.halo_axis.name = 'halo_axis';
        uid_halo_raynode.add(this.halo_axis);
    }

    show_axis() {
        if (this.halo_axis) {
            uid_halo_raynode.remove(this.halo_axis);
            if (!(Array.isArray(this.halo_axis.material)))
                this.halo_axis.material.dispose();
            this.halo_axis.geometry.dispose();
        }
        this.halo_axis_geometry = new CylinderGeometry(viewSize/500, viewSize/500, viewSize / 15.00, 32, 1, false);
        this.halo_axis_geometry.rotateX(Math.PI/2.0);
        const nv = new Vector3(0, 0, 1);
        const R = rotationMatrixBetweenVectors(nv, this.normal);
        // Rotate geometries.
        const m4 = new Matrix4();
        m4.set(
            R.elements[0]!,
            R.elements[3]!,
            R.elements[6]!,
            0,
            R.elements[1]!,
            R.elements[4]!,
            R.elements[7]!,
            0,
            R.elements[2]!,
            R.elements[5]!,
            R.elements[8]!,
            0,
            0,
            0,
            0,
            1
        );
        this.halo_axis_geometry.applyMatrix4(m4);
        const offset = 0;
        this.halo_axis_geometry.translate(this.center.x + this.normal.x * offset, this.center.y  + this.normal.y * offset, this.center.z + this.normal.z * offset);
        this.halo_material.color.setRGB(0.4, 0.4, .8);
        this.halo_axis = new Mesh(this.halo_axis_geometry, this.halo_material);
        this.halo_axis.name = 'halo_axis';
        uid_halo_raynode.add(this.halo_axis);
    }

    inflate_axis(positive_direction: boolean) {
        if (this.halo_axis) {
            uid_halo_raynode.remove(this.halo_axis);
            if (!Array.isArray(this.halo_axis.material))
                this.halo_axis.material.dispose();
            this.halo_axis.geometry.dispose();
        }
        this.halo_axis_geometry = new SphereGeometry(viewSize / 100.00, 32, 32);
        this.halo_axis_geometry.translate(this.center.x, this.center.y, this.center.z);
        if (positive_direction)
            this.halo_material.color.setRGB(0.4, 0.9, 0.2);
        else
            this.halo_material.color.setRGB(0.9, 0.4, 0.2);
        this.halo_axis = new Mesh(this.halo_axis_geometry, this.halo_material);
        this.halo_axis.name = 'halo_axis';
        uid_halo_raynode.add(this.halo_axis);
    }
}
